import React from 'react';
import { Polygon } from 'ol/geom';
import { Projection } from 'ol/proj';
import ImageLayer from 'ol/layer/Image';
import Static from 'ol/source/ImageStatic';
import { Feature, Overlay, View } from 'ol';
import VectorSource from 'ol/source/Vector';
import { Extent, getCenter } from 'ol/extent';
import GeoImage from 'ol-ext/source/GeoImage';

import { createCustomTaskPosition } from '../../utils/map/task-position';
import { ExportedZoneData } from '../../types/game-document/entities/exported-zone';
import { MapIllustrationContext } from '../../components/map/map-illustration-context';

interface ImageResolution {
  height: number;
  width: number;
}

export const ZoneLibraryIllustrationPreview: React.FC<{
  content: ExportedZoneData;
}> = ({ content }) => {
  const map = React.useContext(MapIllustrationContext);

  const mapRef = React.useRef<HTMLDivElement | null>(null);
  const imageLayersRef = React.useRef<Record<string, ImageLayer<GeoImage>>>({});

  const [imageLayer, setImageLayer] = React.useState<ImageLayer<Static>>();

  const mapEntity = content.maps[0];
  const imageEntities = content.mapImages ?? [];
  const taskEntities = content.tasks ?? [];

  const imageList = React.useMemo(() => {
    const result: any[] = [];

    if (imageEntities.length > 0) {
      imageEntities.forEach((image) => {
        if (image.imageResId) {
          const url =
            content.resources.find((res) => res.id === image.imageResId)
              ?.value ?? '';
          result.push({
            id: `image-${image.id}`,
            url,
            opacity: image.opacity ?? 1,
            center: image.center ?? [0, 0],
            scale: image.scale ?? [100, 100],
            isVisible: image.isVisible,
            isLocked: image.isLocked ?? false
          });
        }
      });
    }

    return result;
  }, [imageEntities]);

  const getBaseImage = (url: string): Promise<ImageResolution> => {
    return new Promise((resolve, reject) => {
      let img = new Image();
      img.src = url;
      img.onload = () => {
        const imgRes: ImageResolution = {
          width: img.width,
          height: img.height
        };
        resolve(imgRes);
      };
      img.onerror = (e) => {
        reject(e);
      };
    });
  };

  React.useEffect(() => {
    const url =
      content.resources.find((res) => res.id === content.maps[0].imageResId)
        ?.value ?? '';

    if (!url || !mapRef) return;

    getBaseImage(url).then((res) => {
      map.setTarget(mapRef.current as HTMLElement);

      let extent: Extent;
      extent = [0, 0, res.width, res.height];
      const projection = new Projection({
        code: 'xkcd-image',
        units: 'pixels',
        extent: extent
      });

      map.setView(
        new View({
          projection: projection,
          center:
            mapEntity.latitude !== undefined &&
            mapEntity.longitude !== undefined
              ? [mapEntity.longitude, mapEntity.latitude]
              : getCenter(extent),
          extent,
          zoom: 1
        })
      );

      if (mapEntity.zoomLevel !== undefined)
        map.getView().setZoom(mapEntity.zoomLevel!);

      const baseImageLayer = new ImageLayer({
        source: new Static({
          attributions: '',
          url: url ?? '',
          projection: projection,
          imageExtent: extent
        })
      });

      setImageLayer(baseImageLayer);

      map.setLayers([baseImageLayer]);

      // render task
      taskEntities.forEach((task) => {
        if (!task.boundary?.geometry?.coordinates) return;
        const imgUrl =
          content.resources.find((res) => res.id === task.imageResId)?.value ??
          '';

        // Fallback to default designer icon if resource not found
        const resolvedImgUrl =
          imgUrl || 'https://cdn.catalystglobal.games/resources/map-task.png';

        const title =
          content.resources.find((res) => res.id === task.titleResId)?.value ??
          '';

        const taskExist = map.getOverlayById(task.id);
        // prevent create task overlay more than once
        if (taskExist) return;

        const taskElement = createCustomTaskPosition({
          id: task.id,
          name: title,
          iconUrl: resolvedImgUrl,
          isAddTaskMode: false,
          showTasknameVisibility: true,
          isVisible: Boolean(task.isVisible)
        });

        const overlay = new Overlay({
          id: task.id,
          position: task.boundary.geometry.coordinates || [0, 0],
          positioning: 'center-center',
          element: taskElement,
          stopEvent: false
        });

        map.addOverlay(overlay);
      });
    });

    return () => {
      map.getOverlays().clear();
      map.getLayers().clear();
      map.setTarget(undefined);
    };
  }, [content]);

  // render image
  React.useEffect(() => {
    if (!map || !imageLayer) return;

    const layers = map.getAllLayers();
    const existingImageLayers = layers.filter((layer) =>
      layer.get('id')?.startsWith('image-')
    );
    existingImageLayers.forEach((layer) => map.removeLayer(layer));
    imageLayersRef.current = {};

    if (!imageList || imageList.length === 0) return;

    const imageLayers: ImageLayer<GeoImage>[] = [];

    // wait for all images to load before adding layers
    const promises = imageList.map(
      (imgData) =>
        new Promise<void>((resolve) => {
          const { id, url, center, scale, opacity = 1, isVisible } = imgData;

          const image = new Image();
          image.src = url;
          image.onload = () => {
            const source = new GeoImage({
              url,
              imageCenter: center,
              imageScale: scale
            });

            const layer = new ImageLayer({
              source,
              properties: { id },
              opacity,
              visible: isVisible
            });

            imageLayers.push(layer);
            imageLayersRef.current[id] = layer;

            resolve();
          };

          image.onerror = () => {
            console.warn(`Failed to load image: ${url}`);
            resolve();
          };
        })
    );

    // once all images are loaded, update the layer order
    Promise.all(promises).then(() => {
      if (imageLayers.length > 0) {
        const tileLayer = layers[0];
        const otherLayers = layers
          .slice(1)
          .filter((layer) => !layer.get('id')?.startsWith('image-'));

        imageLayers.forEach((layer) => {
          const source = layer.getSource();
          const id = layer.get('id');
          if (!source || !id) return;

          const imageExtent = source.getExtent();
          const imageFeature = new Feature({
            geometry: new Polygon([
              [
                [imageExtent[0], imageExtent[1]],
                [imageExtent[0], imageExtent[3]],
                [imageExtent[2], imageExtent[3]],
                [imageExtent[2], imageExtent[1]],
                [imageExtent[0], imageExtent[1]]
              ]
            ])
          });

          imageFeature.setId(id);
          const imageLayer = map
            .getAllLayers()
            .find((l) => l.get('id') === 'vectorImage');

          if (imageLayer) {
            (imageLayer.getSource() as VectorSource).addFeature(imageFeature);
          }
        });

        // new order: tile → images → others
        map.setLayers([tileLayer, ...imageLayers, ...otherLayers]);
      }
    });

    return () => {
      imageLayers.forEach((layer) => map.removeLayer(layer));
      imageLayersRef.current = {};
    };
  }, [
    map,
    imageLayer,
    imageList.map((i) => i.id + i.url + i.opacity + i.center + i.scale).join('')
  ]);

  return (
    <div
      id={'map'}
      ref={mapRef as any}
      style={{
        width: '100%',
        height: '100%'
      }}
    />
  );
};
